import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import { MATERIAL_CATEGORY } from "@/constants/pin";
import imageUrl, { getImageSrc2x } from "@/utils/imageUrl";
import classNames from "classnames";
import { useMemo } from "react";
import styles from "./cardA.module.less";

interface RecommendTopic {
  urlname?: string;
  filter_id: string;
  preview: {
    pin_count: number;
    pins: IPin[];
  };

  origin: { title: string };

  filter: {
    name: string;
    id: number;
  };

  order: number;
  link: string;
  title?: string;
}

enum ViewType {
  bg = "bg",
  vertical = "vertical",
  horizontal = "horizontal",
  square = "square",
}

export function MaterialTopicCard({
  title,
  pinCount,
  pins,
  filter,
  link,
  ...rest
}: {
  title: string;
  pinCount: number;
  pins: IPin[];
  filter: RecommendTopic["filter"];
  link: string;
}) {
  const firstPin = useMemo(() => {
    const materialType = pins[0].file_material?.material_category;
    if (MATERIAL_CATEGORY.miankou === materialType) {
      return pins.find((pin) => pin.file.width === pin.file.height) || pins[0];
    }

    return pins[0];
  }, [pins]);

  const viewType = useMemo(() => {
    if (title.includes("背景")) return ViewType.bg;

    if (!firstPin) {
      return ViewType.square;
    }

    const materialType = firstPin.file_material?.material_category;

    if (MATERIAL_CATEGORY.miankou === materialType) {
      return ViewType.square;
    }
    // 竖图海报
    if (firstPin.file.width / firstPin.file.height < 0.6) {
      return ViewType.vertical;
    }

    //横版海报
    if (firstPin.file.width / firstPin.file.height > 1.7) {
      return ViewType.horizontal;
    }

    return ViewType.square;
  }, [firstPin, title]);

  return (
    <a
      href={link}
      className={styles.card}
      target="_blank"
      {...rest}
      style={{
        backgroundColor: firstPin.file?.theme,
      }}
    >
      <div className={styles.left}>
        <div className={styles.title}>{title}</div>
        <div className={styles.count}>
          {pinCount}张
          <HBIcon type="ic_chevron_right" />
        </div>
        <div className={styles.text}>
          立即查看
          <HBIcon type="ic_chevron_right" />
        </div>
      </div>
      <div
        className={classNames(
          styles.bgImg,
          (viewType === ViewType.bg || viewType === ViewType.square) &&
            styles.noBlur
        )}
        style={
          viewType !== ViewType.square
            ? {
                backgroundImage: `url("${imageUrl(firstPin.file, {
                  width: 240,
                })}")`,
              }
            : null
        }
      ></div>
      {/* 右侧展示图片，hover隐藏 */}
      <div className={styles.rightImg}>
        {viewType === ViewType.vertical && (
          <div className={styles.verticalImg}>
            <Image
              {...getImageSrc2x(pins[0].file, { width: 84 })}
              width={70}
              height={120}
              alt={firstPin.raw_text}
            />
            <Image
              {...getImageSrc2x(pins[1].file, { width: 84 })}
              width={60}
              height={120}
              alt={firstPin.raw_text}
            />
          </div>
        )}
        {viewType === ViewType.square && (
          <div className={styles.squareImg}>
            <Image
              {...getImageSrc2x(firstPin.file, { width: 84 })}
              width={84}
              height={84}
              alt={firstPin.raw_text}
            />
          </div>
        )}
        {viewType === ViewType.horizontal && (
          <div className={styles.horizontalImg}>
            <Image
              {...getImageSrc2x(firstPin.file, { width: 138 })}
              width={138}
              height={64}
              alt={firstPin.raw_text}
            />
            <Image
              {...getImageSrc2x(firstPin.file, { width: 138 })}
              width={138}
              height={64}
              alt={firstPin.raw_text}
            />
            <Image
              {...getImageSrc2x(firstPin.file, { width: 138 })}
              width={138}
              height={64}
              alt={firstPin.raw_text}
            />
          </div>
        )}
      </div>
      {/* hover预览图 */}
      <div className={classNames(styles.preview, styles[viewType])}>
        {pins.map((item) => {
          return (
            <Image
              key={item.pin_id}
              {...getImageSrc2x(item.file, { width: 109 })}
              alt=""
              width={viewType === ViewType.horizontal ? 107 : 52}
              height={viewType === ViewType.vertical ? 107 : 46}
            />
          );
        })}
      </div>
    </a>
  );
}
